import {
	Card,
	CardBody,
	CardFooter,
	DataListHeader,
	DataListSearch,
	IconButton,
	Pagination,
	SwitchControl,
	useEditDrawer,
	usePaginatedList,
} from "@dgs/core";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import styled from "styled-components";
import { useFilterQuery } from "../../../../../shared/api/filterQueries";
import { CreateEventDrawer } from "../../../../../shared/events/save/CreateEventDrawer";
import { useFilter } from "../../../../../shared/filters";
import { EventFilterDrawer } from "../../../../../shared/filters/events/EventFilterDrawer";
import { FilterBar } from "../../../../../shared/filters/FilterBar";
import { EventListOverviewResource } from "../../../../../shared/types/event";
import { eventService } from "../../../shared/api/event/eventService";
import { EventExportAction } from "../../EventExportAction";
import { useDeleteEvent } from "../../useDeleteEvent";
import { useEventSave } from "../../useEventSave";
import { EventScopeProps } from "../EventsOverview";
import { OverviewContextMenu } from "./OverviewContextMenu";
import { OverviewDataList } from "./OverviewDataList";

const StyledSwitchControl = styled(SwitchControl)`
	margin: 0;
`;

export const EventListOverview: FC<EventScopeProps> = ({ showOnlyOwnEvents, onToggle }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const deleteEvent = useDeleteEvent();
	const {
		data: events,
		loading,
		setPage,
		pagination,
		isEmpty,
		setSearchParams,
	} = usePaginatedList<EventListOverviewResource>();
	const { activeFilterId, resetFilter, applyFilter } = useFilter();
	const handleSave = useEventSave();
	const filterDrawerProps = useEditDrawer();
	const { data: predefinedFilter } = useFilterQuery({ filterId: activeFilterId });
	const openFilterDrawer = useCallback(() => {
		filterDrawerProps.handleOpen(activeFilterId || "new");
	}, [activeFilterId, filterDrawerProps]);

	return (
		<>
			<Card>
				<DataListHeader
					loading={loading}
					heading={t("Events list")}
					actions={
						<>
							<StyledSwitchControl
								name="showOnlyOwnEvents"
								label={t("Only my events")}
								value={showOnlyOwnEvents}
								onChange={onToggle}
							/>
							<DataListSearch
								setSearchParams={setSearchParams}
								searchParams={{
									search: "",
									searchFields: [
										{ label: t("Name"), name: "event_translations.name" },
										{ label: t("Project number"), name: "projectNumber" },
										{ label: t("Project managers"), name: "projectManagers" },
									],
								}}
							/>
							<CreateEventDrawer createPath="/events/create" detailsPath="/events/active" handleSubmit={handleSave} />
							<IconButton title={t("Open event filter")} onClick={openFilterDrawer} size="small" icon="filter" />
							<EventExportAction
								filterId={activeFilterId}
								onExport={eventService.exportEvents}
								showOnlyOwnEvents={showOnlyOwnEvents}
							/>
						</>
					}
				>
					<FilterBar filterId={activeFilterId} onRemove={resetFilter} onClick={openFilterDrawer} />
				</DataListHeader>
				<CardBody>
					<OverviewDataList
						events={events}
						isEmpty={isEmpty}
						onRowClick={(event) => navigate(`/events/active/${event.id}/details`)}
						actions={(event) => (
							<OverviewContextMenu
								onEdit={() => navigate(`/events/active/${event.id}`)}
								deleteEvent={() => deleteEvent(event.id)}
								duplicateEvent={() => navigate(`/events/create/from-event/${event.id}/base-data`)}
							/>
						)}
					/>
				</CardBody>
				<CardFooter>
					<Pagination pagination={pagination} onChange={setPage} />
				</CardFooter>
			</Card>
			{filterDrawerProps.editEntityId && (
				<EventFilterDrawer {...filterDrawerProps} filter={predefinedFilter || null} onSubmit={applyFilter} />
			)}
		</>
	);
};
